import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingV2 from 'components/LoadingV2'
import PMBModalImagePreview from 'components/PMBModalImagePreview'
import PMBPagination from 'components/PMBPagination'
import PMBProgressBarExportFile from 'components/PMBProgressBarExportFile'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import { handleDownloadExcelWithRTK } from 'features/private-views/warehouse/detail/downloadWarehouse'
import FileSaver from 'file-saver'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect'
import { capitalizeEveryWord } from 'helpers'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import {
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  Calendar,
  ChevronDown,
  FileText,
  Info,
  Package,
} from 'react-feather'
import Flatpickr from 'react-flatpickr'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import { Card, CardBody, Spinner } from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_INVENTORI_INTELLIGENCE } from 'utils/pathUrl'
import * as XLSX from 'xlsx-js-style'
import { styleTableSheet } from 'utils/excelStyle'
import {
  useFetchWarehouseNetworkByIdQuery,
  useFetchWarehouseNetworkSummaryQuery,
  useLazyFetchWarehouseNetworkSummaryQuery,
} from '../inventoriIntelligentApiSlice'

const DetailInventoriIntelligent = () => {
  const { id } = useParams()
  const intl = useIntl()
  const location = useLocation()
  const dispatch = useDispatch()
  const searchParams = new URLSearchParams(location?.search)
  const ownerUserId = searchParams.get('ownerUserId')
  const userIdParam = searchParams.get('userId')
  const { itemsFilter } = useSelector((s) => s.inventoriIntelligent)

  const MIN_MAX_HEIGHT = '100px'
  const [activeTab, setActiveTab] = useState('monthly')
  const [currentPage, setCurrentPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [sortColumn, setSortColumn] = useState('nameItem')
  const [sortDirection, setSortDirection] = useState('desc')
  const [imageItems, setImagesItems] = useState([])
  const [modalOpenImage, setModalOpenImage] = useState(false)

  const [isDownload, setIsDownload] = useState(false)
  const [firstRows, setFirstRows] = useState(0)
  const [totalRows, setTotalRows] = useState(0)
  const [monthlyData, setMonthlyData] = useState([])
  const [monthlyLoading, setMonthlyLoading] = useState(false)

  // FETCH API BY WAREHOUSE DETAIL
  const formattedDate = moment(selectedDate).format('YYYY-MM')
  const formattedMonthLabel = moment(selectedDate).format('MMMM YYYY')
  const { data: dataById, isLoading: isLoadingById } =
    useFetchWarehouseNetworkByIdQuery({
      id: id,
      ...(userIdParam && { userIdParam }),
    })

  const dataWarehouse = dataById?.items?.[0]

  const { data, isLoading, isFetching } = useFetchWarehouseNetworkSummaryQuery(
    {
      page: currentPage ?? 0,
      size: rowsPerPage ?? 10,
      sortby: sortColumn,
      sortdir: sortDirection,
      warehouseId: id ?? null,
      month: formattedDate,
      type: activeTab === 'all' ? 'current' : activeTab,
      ...(ownerUserId && { ownerUserId }),
      ...(itemsFilter?.length > 0 && {
        itemIds: `[${itemsFilter.map((item) => item.value).join(',')}]`,
      }),
    },
    { skip: activeTab === 'monthly' },
  )

  const [triggerFetchForDownload] = useLazyFetchWarehouseNetworkSummaryQuery()

  const fetchMonthlyReport = async (monthValue) => {
    if (!id) return
    setMonthlyLoading(true)
    try {
      const paramsBase = (type) => ({
        warehouseId: id ?? null,
        month: monthValue,
        type,
        page: 0,
        size: 9999,
        ...(ownerUserId && { ownerUserId }),
        ...(itemsFilter?.length > 0 && {
          itemIds: `[${itemsFilter.map((item) => item.value).join(',')}]`,
        }),
      })

      const prevMonth = moment(monthValue, 'YYYY-MM')
        .subtract(1, 'month')
        .format('YYYY-MM')

      const [resBal, resIn, resOut] = await Promise.all([
        triggerFetchForDownload({ ...paramsBase('balance'), month: prevMonth }),
        triggerFetchForDownload(paramsBase('in')),
        triggerFetchForDownload(paramsBase('out')),
      ])

      const balItems = resBal?.data?.items || []
      const inItems = resIn?.data?.items || []
      const outItems = resOut?.data?.items || []

      const map = new Map()

      const addToMap = (arr, field) => {
        arr.forEach((r) => {
          const nameRaw = r?.nameItem || r?.item?.nameItem || '-'
          const unitRaw = r?.unit || r?.item?.unit || ''
          const name = nameRaw.trim()
          const unitNormalized = unitRaw.trim().toLowerCase()
          const displayUnit = unitNormalized
          const key = `${name}-${unitNormalized}`

          if (!map.has(key)) {
            map.set(key, {
              nameItem: name,
              unit: capitalizeEveryWord(displayUnit),
            })
          }
          const entry = map.get(key)
          entry[field] = Number(r?.total ?? r?.initialAmount ?? 0)
          if (r?.totalValue !== undefined) {
            entry['totalValue'] = Number(r.totalValue ?? 0)
          }
          map.set(key, entry)
        })
      }

      addToMap(balItems, 'balanceMMinus1')
      addToMap(inItems, 'incoming')
      addToMap(outItems, 'outgoing')

      const rows = Array.from(map.entries()).map(([k, v]) => {
        const incoming = Number(v.incoming || 0)
        const outgoing = Number(v.outgoing || 0)
        const balanceMMinus1 = Number(v.balanceMMinus1 || 0)

        // Last column: Balance = first balance + in - out
        const balanceM = balanceMMinus1 + incoming - outgoing

        return {
          key: k,
          nameItem: v.nameItem || '-',
          unit: v.unit || '',
          balanceMMinus1,
          incoming,
          outgoing,
          balanceM,
          totalValue: Number(v.totalValue || 0),
        }
      })

      setMonthlyData(rows)
      setCurrentPage(0)
    } catch (e) {
      console.error('Failed to fetch monthly report', e)
      setMonthlyData([])
    } finally {
      setMonthlyLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'monthly') {
      fetchMonthlyReport(formattedDate)
    }
  }, [activeTab, formattedDate, id])

  const dataResponse = Array.isArray(data?.items) ? data?.items : []

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length > 0) {
      const date = selectedDates[0]
      setSelectedDate(date)
    }
  }

  // HANDLE SORTING
  const handleSort = (column, direction) => {
    setSortColumn(column.selector)
    setSortDirection(direction === 'asc' ? 'asc' : 'desc')
  }

  // HANDLE DOWNLOAD WITH DYNAMIC PAGINATION LOOP
  const handleDownloadClick = async () => {
    if (activeTab === 'monthly') {
      // Export the computed monthly data
      try {
        const prevMonthLabel = moment(selectedDate)
          .subtract(1, 'month')
          .format('MMMM YYYY')
        const wb = XLSX.utils.book_new()
        const header = [
          'Jenis Barang',
          `Balance (Akhir ${prevMonthLabel})`,
          `Barang Masuk (${formattedMonthLabel})`,
          `Barang Keluar (${formattedMonthLabel})`,
          `Balance Akhir (${formattedMonthLabel})`,
          `Nilai (${formattedMonthLabel})`,
          'Satuan',
        ]
        const ws = XLSX.utils.aoa_to_sheet([header])
        const rows = (monthlyData || []).map((r) => [
          r.nameItem || '-',
          r.balanceMMinus1 ?? 0,
          r.incoming ?? 0,
          r.outgoing ?? 0,
          r.balanceM ?? 0,
          r.totalValue ?? 0,
          r.unit || 'pcs',
        ])
        XLSX.utils.sheet_add_aoa(ws, rows, { origin: 'A2' })
        XLSX.utils.book_append_sheet(wb, styleTableSheet(ws), 'Laporan Bulanan')
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const finalData = new Blob([excelBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
        })
        FileSaver.saveAs(
          finalData,
          `Laporan_Bulanan_${dataWarehouse?.name ?? 'gudang'}_${formattedDate}.xlsx`,
        )
      } catch (e) {
        console.error('Export monthly failed', e)
      }
      return
    }

    const baseQueryParams = {
      sortby: 'nameItem',
      sortdir: 'asc',
      warehouseId: id ?? null,
      month: formattedDate,
      type: activeTab === 'all' ? 'current' : activeTab,
      ...(ownerUserId && { ownerUserId }),
      ...(itemsFilter?.length > 0 && {
        itemIds: `[${itemsFilter.map((item) => item.value).join(',')}]`,
      }),
    }

    await handleDownloadExcelWithRTK(
      dataWarehouse?.name ?? '-',
      activeTab,
      data,
      setIsDownload,
      setFirstRows,
      setTotalRows,
      triggerFetchForDownload,
      baseQueryParams,
      rowsPerPage,
    )
  }

  const columnsMonthly = React.useMemo(
    () => [
      {
        name: <span>Jenis Barang</span>,
        selector: (row) => row?.nameItem,
        sortable: true,
        width: '300px',
        cell: (row) => <span>{row?.nameItem}</span>,
      },
      {
        name: (
          <div className="d-flex align-items-center gap-1">
            <span>Balance</span>
            <span id={`info-balance-m1-${id}`} style={{ cursor: 'pointer' }}>
              <Info size={14} />
            </span>
            <PMBTooltip
              target={`info-balance-m1-${id}`}
              text={`Balance pada akhir bulan sebelumnya. Digunakan sebagai saldo awal untuk perhitungan bulan ini.`}
            />
          </div>
        ),
        selector: (row) => row?.balanceMMinus1,
        sortable: true,
        right: true,
        cell: (row) => (
          <div className="d-flex justify-content-end w-100">
            <span>
              {newFormatThousandSeparator(row?.balanceMMinus1 ?? 0, 2)}{' '}
              {row?.unit ?? row?.item?.unit ?? ''}
            </span>
          </div>
        ),
      },
      {
        name: (
          <div className="d-flex align-items-center gap-1">
            <span>Barang Masuk</span>
            <span id={`info-incoming-${id}`} style={{ cursor: 'pointer' }}>
              <Info size={14} />
            </span>
            <PMBTooltip
              target={`info-incoming-${id}`}
              text={`Total jumlah barang yang masuk ke gudang selama bulan ${formattedMonthLabel}.`}
            />
          </div>
        ),
        selector: (row) => row?.incoming,
        sortable: true,
        right: true,
        cell: (row) => (
          <div className="d-flex justify-content-end w-100">
            <span>
              {newFormatThousandSeparator(row?.incoming ?? 0, 2)}{' '}
              {row?.unit ?? ''}
            </span>
          </div>
        ),
      },
      {
        name: (
          <div className="d-flex align-items-center gap-1">
            <span>Barang Keluar</span>
            <span id={`info-outgoing-${id}`} style={{ cursor: 'pointer' }}>
              <Info size={14} />
            </span>
            <PMBTooltip
              target={`info-outgoing-${id}`}
              text={`Total jumlah barang yang keluar dari gudang selama bulan ${formattedMonthLabel}.`}
            />
          </div>
        ),
        selector: (row) => row?.outgoing,
        sortable: true,
        right: true,
        cell: (row) => (
          <div className="d-flex justify-content-end w-100">
            <span>
              {newFormatThousandSeparator(row?.outgoing ?? 0, 2)}{' '}
              {row?.unit ?? ''}
            </span>
          </div>
        ),
      },
      {
        name: (
          <div className="d-flex align-items-center gap-1">
            <span>Balance Akhir</span>
            <span id={`info-balance-m-${id}`} style={{ cursor: 'pointer' }}>
              <Info size={14} />
            </span>
            <PMBTooltip
              target={`info-balance-m-${id}`}
              text={`Saldo pada akhir ${formattedMonthLabel} (atau balance pada tanggal berjalan jika memilih hari ini).`}
            />
          </div>
        ),
        selector: (row) => row?.balanceM,
        sortable: true,
        // width: '150px',
        right: true,
        cell: (row) => (
          <div className="d-flex justify-content-end w-100">
            <span>
              {newFormatThousandSeparator(row?.balanceM ?? 0, 2)}{' '}
              {row?.unit ?? ''}
            </span>
          </div>
        ),
      },
      {
        name: (
          <div className="d-flex align-items-center gap-1">
            <span>Nilai</span>
            <span id={`info-nilai-${id}`} style={{ cursor: 'pointer' }}>
              <Info size={14} />
            </span>
            <PMBTooltip
              target={`info-nilai-${id}`}
              text={`Total nilai barang pada akhir ${formattedMonthLabel}.`}
            />
          </div>
        ),
        selector: (row) => row?.totalValue,
        sortable: true,
        right: true,
        cell: (row) => (
          <div className="d-flex justify-content-end w-100">
            <span>Rp{newFormatThousandSeparator(row?.totalValue ?? 0, 2)}</span>
          </div>
        ),
      },
    ],
    [formattedMonthLabel, id],
  )

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#F9FAFB',
      },
    },
    cells: {
      style: {
        marginTop: '1px',
        marginBottom: '1px',
      },
    },
  }

  const paginatedMonthlyData = React.useMemo(() => {
    const start = currentPage * rowsPerPage
    const end = start + rowsPerPage
    return monthlyData.slice(start, end)
  }, [monthlyData, currentPage, rowsPerPage])

  const monthlySummary = React.useMemo(() => {
    if (!monthlyData || monthlyData.length === 0)
      return { totalItems: 0, totalsByUnit: {} }
    const totalItems = monthlyData.length
    const totalsByUnit = {}
    monthlyData.forEach((r) => {
      const unit = (r?.unit || 'pcs').toLowerCase()
      if (!totalsByUnit[unit])
        totalsByUnit[unit] = {
          incoming: 0,
          outgoing: 0,
          balanceEnd: 0,
          totalValue: 0,
        }
      totalsByUnit[unit].incoming += Number(r.incoming || 0)
      totalsByUnit[unit].outgoing += Number(r.outgoing || 0)
      totalsByUnit[unit].balanceEnd += Number(r.balanceM || 0)
      totalsByUnit[unit].totalValue += Number(r.totalValue || 0)
    })
    return { totalItems, totalsByUnit }
  }, [monthlyData])

  if (isLoadingById) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      {/* BREADCRUMB */}
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs
          breadCrumbParent2="List Inventory intelligence"
          breadCrumbParent2Link={URL_INVENTORI_INTELLIGENCE}
          breadCrumbActive={`${dataWarehouse?.name ?? 'Detail Inventory Intelligence'}`}
        />
      </div>
      {/* END BREADCRUMB */}

      {/* PROGRESS DOWNLOAD */}
      <PMBProgressBarExportFile
        isDialogShow={isDownload}
        firstRows={firstRows}
        totalRows={totalRows}
        onCancel={() => setIsDownload(false)}
      />

      {/* MODAL IMAGE PREVIEW */}
      <PMBModalImagePreview
        isOpen={modalOpenImage}
        toggle={() => setModalOpenImage(!modalOpenImage)}
        images={imageItems}
      />
      {/* END MODAL IMAGE PREVIEW */}

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <div className="row">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-3">
              <div
                className="d-flex flex-column gap-1"
                style={{ minWidth: '300px' }}
              >
                <h5 className="fw-semibold mb-0">
                  {dataWarehouse?.name ?? '-'}
                </h5>
                {/* <div
                  className="fw-normal text-muted"
                  style={{ fontSize: '0.875rem' }}
                >
                  Alamat: {dataWarehouse?.address ?? '-'}
                </div> */}
              </div>

              <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto justify-content-start justify-content-md-end">
                <CustomButton
                  disabled={
                    isDownload || monthlyLoading || !(monthlyData?.length > 0)
                  }
                  className="rounded w-100 w-sm-auto btn-download"
                  onClick={handleDownloadClick}
                  color="primary"
                  outline
                  size="sm"
                >
                  {isDownload ? (
                    <Spinner size="sm" color="primary" />
                  ) : (
                    <FileText size={16} className="me-1" />
                  )}{' '}
                  Unduh
                </CustomButton>

                <div className="w-100 w-sm-auto date-filter">
                  <div className="d-flex align-items-center border rounded px-2">
                    <Calendar size={17} className="me-2" />
                    <Flatpickr
                      value={selectedDate}
                      onChange={handleDateChange}
                      options={{
                        altInput: true,
                        altFormat: 'F Y',
                        dateFormat: 'Y-m',
                        defaultDate: selectedDate,
                        plugins: [
                          new MonthSelectPlugin({
                            shorthand: false,
                            dateFormat: 'Y-m',
                            altFormat: 'F Y',
                          }),
                        ],
                      }}
                      className="form-control bg-transparent border-0 shadow-none flex-grow-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* TABLE */}
            <div>
              <DataTable
                key={`${currentPage}-${rowsPerPage}-${activeTab}`}
                customStyles={customStyles}
                columns={columnsMonthly}
                data={paginatedMonthlyData}
                progressPending={monthlyLoading}
                responsive
                persistTableHead
                defaultSortFieldId={1}
                defaultSortAsc={false}
                sortIcon={<ChevronDown />}
                sortServer={false}
                onSort={handleSort}
                className="border p-0 border-1 rounded-top"
                progressComponent={
                  <div style={{ margin: '100px 0px' }}>
                    <Spinner color="primary" />
                  </div>
                }
                noDataComponent={
                  <p className="my-3 px-3 fw-semibold">
                    Tidak ada data inventori{' '}
                    {activeTab === 'in'
                      ? 'masuk'
                      : activeTab === 'out'
                        ? 'keluar'
                        : activeTab === 'monthly'
                          ? 'untuk laporan bulanan'
                          : ''}{' '}
                    yang tersedia
                  </p>
                }
                pagination
                paginationServer={false}
                paginationTotalRows={monthlyData?.length || 0}
                paginationPerPage={rowsPerPage}
                paginationPageNumber={currentPage + 1}
                paginationComponent={(props) => (
                  <PMBPagination
                    rowCount={monthlyData?.length || 0}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    setCurrentPage={setCurrentPage}
                    setRowsPerPage={setRowsPerPage}
                    props={props}
                  />
                )}
              />
            </div>
            {/* END TABLE */}
          </div>
        </CardBody>
      </Card>

      {/* MONTHLY SUMMARY - New Card Design */}
      {activeTab === 'monthly' && (
        <div className="mt-4">
          <div className="row g-3">
            {/* Card 1: Total Inventory */}
            <div className="col-12 col-md-6 col-xl-3">
              <Card className="border-0 shadow-sm h-100">
                <CardBody className="d-flex align-items-center">
                  <div
                    className="rounded-circle bg-light-primary p-3 me-3 d-flex align-items-center justify-content-center"
                    style={{ width: '60px', height: '60px' }}
                  >
                    <Package className="text-primary" size={24} />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Total Inventory</h6>
                    <h4 className="fw-bold mb-0">
                      {monthlySummary.totalItems}
                    </h4>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Card 2: Barang Masuk */}
            <div className="col-12 col-md-6 col-xl-3">
              <Card className="border-0 shadow-sm h-100">
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle bg-light-success p-2 me-2 d-flex align-items-center justify-content-center">
                      <ArrowDownLeft className="text-success" size={18} />
                    </div>
                    <h6 className="fw-bold mb-0">Barang Masuk</h6>
                  </div>
                  <div
                    style={{ maxHeight: '150px', overflowY: 'auto' }}
                    className="hide-scrollbar"
                  >
                    {Object.keys(monthlySummary.totalsByUnit || {}).length >
                    0 ? (
                      Object.keys(monthlySummary.totalsByUnit || {}).map(
                        (unit) => (
                          <div
                            key={`in-${unit}`}
                            className="d-flex justify-content-between align-items-center mb-1 border-bottom pb-1 last-no-border"
                          >
                            <span className="text-muted small text-uppercase">
                              {unit}
                            </span>
                            <span className="fw-semibold text-success">
                              +
                              {newFormatThousandSeparator(
                                monthlySummary.totalsByUnit[unit].incoming,
                                2,
                              )}
                            </span>
                          </div>
                        ),
                      )
                    ) : (
                      <span className="text-muted small">-</span>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Card 3: Barang Keluar */}
            <div className="col-12 col-md-6 col-xl-3">
              <Card className="border-0 shadow-sm h-100">
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle bg-light-danger p-2 me-2 d-flex align-items-center justify-content-center">
                      <ArrowUpRight className="text-danger" size={18} />
                    </div>
                    <h6 className="fw-bold mb-0">Barang Keluar</h6>
                  </div>
                  <div
                    style={{ maxHeight: '150px', overflowY: 'auto' }}
                    className="hide-scrollbar"
                  >
                    {Object.keys(monthlySummary.totalsByUnit || {}).length >
                    0 ? (
                      Object.keys(monthlySummary.totalsByUnit || {}).map(
                        (unit) => (
                          <div
                            key={`out-${unit}`}
                            className="d-flex justify-content-between align-items-center mb-1 border-bottom pb-1 last-no-border"
                          >
                            <span className="text-muted small text-uppercase">
                              {unit}
                            </span>
                            <span className="fw-semibold text-danger">
                              -
                              {newFormatThousandSeparator(
                                monthlySummary.totalsByUnit[unit].outgoing,
                                2,
                              )}
                            </span>
                          </div>
                        ),
                      )
                    ) : (
                      <span className="text-muted small">-</span>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Card 4: Balance Akhir */}
            <div className="col-12 col-md-6 col-xl-3">
              <Card className="border-0 shadow-sm h-100">
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle bg-light-info p-2 me-2 d-flex align-items-center justify-content-center">
                      <Activity className="text-info" size={18} />
                    </div>
                    <h6 className="fw-bold mb-0">Balance Akhir</h6>
                  </div>
                  <div
                    style={{ maxHeight: '150px', overflowY: 'auto' }}
                    className="hide-scrollbar"
                  >
                    {Object.keys(monthlySummary.totalsByUnit || {}).length >
                    0 ? (
                      Object.keys(monthlySummary.totalsByUnit || {}).map(
                        (unit) => (
                          <div
                            key={`bal-${unit}`}
                            className="d-flex justify-content-between align-items-center mb-1 border-bottom pb-1 last-no-border"
                          >
                            <span className="text-muted small text-uppercase">
                              {unit}
                            </span>
                            <span className="fw-bold text-dark">
                              {newFormatThousandSeparator(
                                monthlySummary.totalsByUnit[unit].balanceEnd,
                                2,
                              )}
                            </span>
                          </div>
                        ),
                      )
                    ) : (
                      <span className="text-muted small">-</span>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Card 5: Nilai */}
            {/* <div className="col-12 col-md-6 col-xl-3">
              <Card className="border-0 shadow-sm h-100">
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle bg-light-warning p-2 me-2 d-flex align-items-center justify-content-center">
                      <FileText className="text-warning" size={18} />
                    </div>
                    <h6 className="fw-bold mb-0">Nilai</h6>
                  </div>
                  <div
                    style={{ maxHeight: '150px', overflowY: 'auto' }}
                    className="hide-scrollbar"
                  >
                    {Object.keys(monthlySummary.totalsByUnit || {}).length >
                    0 ? (
                      Object.keys(monthlySummary.totalsByUnit || {}).map(
                        (unit) => (
                          <div
                            key={`nilai-${unit}`}
                            className="d-flex justify-content-between align-items-center mb-1 border-bottom pb-1 last-no-border"
                          >
                            <span className="text-muted small text-uppercase">
                              {unit}
                            </span>
                            <span className="fw-bold text-warning">
                              {newFormatThousandSeparator(
                                monthlySummary.totalsByUnit[unit].totalValue,
                                2,
                              )}
                            </span>
                          </div>
                        ),
                      )
                    ) : (
                      <span className="text-muted small">-</span>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div> */}
          </div>
        </div>
      )}
    </div>
  )
}

export default DetailInventoriIntelligent
